import SpeedTest, { type BandwidthPoint, type Results } from '@cloudflare/speedtest';
import { DOWNLOAD_PATH, UPLOAD_PATH } from '../shared/limits';
import { SPIKE_ENGINE_CONFIG } from './config';
import { captureEnvironment, newRunId, type EnvironmentInfo } from './environment';

export type RunStatus = 'running' | 'finished' | 'error' | 'cancelled';

export interface RequestEntry {
  path: string;
  query: string;
  startMs: number;
  durationMs: number;
  transferSize: number;
  encodedBodySize: number;
  decodedBodySize: number;
  protocol: string;
  responseStatus: number | null;
}

export interface CloudflareMetrics {
  downloadMbps: number | null;
  uploadMbps: number | null;
  idleLatencyMs: number | null;
  idleJitterMs: number | null;
  downloadLoadedLatencyMs: number | null;
  downloadLoadedJitterMs: number | null;
  uploadLoadedLatencyMs: number | null;
  uploadLoadedJitterMs: number | null;
}

export interface CloudflareRun {
  schema: 'measurement-spike/v1';
  kind: 'cloudflare-engine';
  id: string;
  label: string;
  startedAt: string;
  environment: EnvironmentInfo;
  engine: { name: '@cloudflare/speedtest'; version: string };
  config: typeof SPIKE_ENGINE_CONFIG;
  status: RunStatus;
  /** Wall clock from play() to finish/error/cancel, measured by this page. */
  wallClockMs: number | null;
  /** Engine-reported duration excluding paused time; only set when the engine finishes. */
  engineTotalDurationMs: number | null;
  metrics: CloudflareMetrics;
  points: {
    idleLatencyMs: number[];
    downloadLoadedLatencyMs: number[];
    uploadLoadedLatencyMs: number[];
    download: BandwidthPoint[];
    upload: BandwidthPoint[];
  };
  phases: Array<{ index: number; type: string; bytes?: number; count?: number; numPackets?: number; atMs: number }>;
  requests: RequestEntry[];
  errors: Array<{ atMs: number; message: string; status: number | null }>;
}

export interface CloudflareRunHandle {
  run: CloudflareRun;
  cancel: () => void;
  done: Promise<CloudflareRun>;
}

const orNull = (value: number | null | undefined): number | null => value ?? null;

// The engine's percentile() returns 0 for an empty sample set, so "no samples" must not be read as 0 ms / 0 Mbps.
const ifSampled = (samples: unknown[], value: number | null | undefined): number | null =>
  samples.length ? orNull(value) : null;
const toMbps = (bps: number | undefined): number | null => (bps ? bps / 1e6 : null);

export function readMetrics(results: Results): CloudflareMetrics {
  return {
    downloadMbps: toMbps(results.getDownloadBandwidth()),
    uploadMbps: toMbps(results.getUploadBandwidth()),
    idleLatencyMs: ifSampled(results.getUnloadedLatencyPoints(), results.getUnloadedLatency()),
    idleJitterMs: orNull(results.getUnloadedJitter()),
    downloadLoadedLatencyMs: ifSampled(results.getDownLoadedLatencyPoints(), results.getDownLoadedLatency()),
    downloadLoadedJitterMs: orNull(results.getDownLoadedJitter()),
    uploadLoadedLatencyMs: ifSampled(results.getUpLoadedLatencyPoints(), results.getUpLoadedLatency()),
    uploadLoadedJitterMs: orNull(results.getUpLoadedJitter()),
  };
}

function toRequestEntry(entry: PerformanceResourceTiming, t0: number): RequestEntry | undefined {
  const url = new URL(entry.name);
  if (url.origin !== location.origin || (url.pathname !== DOWNLOAD_PATH && url.pathname !== UPLOAD_PATH)) {
    return undefined;
  }
  return {
    path: url.pathname,
    query: url.search,
    startMs: entry.startTime - t0,
    durationMs: entry.duration,
    transferSize: entry.transferSize,
    encodedBodySize: entry.encodedBodySize,
    decodedBodySize: entry.decodedBodySize,
    protocol: entry.nextHopProtocol,
    responseStatus: 'responseStatus' in entry ? (entry.responseStatus as number) : null,
  };
}

export function startCloudflareRun(label: string, onUpdate: (run: CloudflareRun) => void): CloudflareRunHandle {
  const t0 = performance.now();
  const engine = new SpeedTest(SPIKE_ENGINE_CONFIG);
  const run: CloudflareRun = {
    schema: 'measurement-spike/v1',
    kind: 'cloudflare-engine',
    id: newRunId(),
    label,
    startedAt: new Date().toISOString(),
    environment: captureEnvironment(),
    engine: { name: '@cloudflare/speedtest', version: __SPEEDTEST_VERSION__ },
    config: SPIKE_ENGINE_CONFIG,
    status: 'running',
    wallClockMs: null,
    engineTotalDurationMs: null,
    metrics: readMetrics(engine.results),
    points: { idleLatencyMs: [], downloadLoadedLatencyMs: [], uploadLoadedLatencyMs: [], download: [], upload: [] },
    phases: [],
    requests: [],
    errors: [],
  };

  // The engine clears the resource-timing buffer between rounds, so capture entries as they arrive.
  const recordEntries = (entries: PerformanceEntryList) => {
    for (const entry of entries) {
      const request = toRequestEntry(entry as PerformanceResourceTiming, t0);
      if (request) run.requests.push(request);
    }
  };
  const observer = new PerformanceObserver((list) => recordEntries(list.getEntries()));
  observer.observe({ type: 'resource' });

  let resolveDone: (value: CloudflareRun) => void = () => {};
  const done = new Promise<CloudflareRun>((resolve) => {
    resolveDone = resolve;
  });

  const collect = () => {
    const results = engine.results;
    run.metrics = readMetrics(results);
    run.points = {
      idleLatencyMs: results.getUnloadedLatencyPoints(),
      downloadLoadedLatencyMs: results.getDownLoadedLatencyPoints(),
      uploadLoadedLatencyMs: results.getUpLoadedLatencyPoints(),
      download: results.getDownloadBandwidthPoints(),
      upload: results.getUploadBandwidthPoints(),
    };
  };

  const finish = (status: Exclude<RunStatus, 'running'>) => {
    if (run.status !== 'running') return;
    run.status = status;
    run.wallClockMs = performance.now() - t0;
    run.engineTotalDurationMs = orNull(engine.results.getTotalDurationMs());
    recordEntries(observer.takeRecords());
    observer.disconnect();
    collect();
    onUpdate(run);
    resolveDone(run);
  };

  engine.onPhaseChange = ({ measurementId, measurement }) => {
    run.phases.push({ index: measurementId, ...measurement, atMs: performance.now() - t0 });
    collect();
    onUpdate(run);
  };
  engine.onFinish = () => finish('finished');
  engine.onError = (message, status) => {
    run.errors.push({ atMs: performance.now() - t0, message, status: status ?? null });
    finish('error');
  };

  engine.play();

  return {
    run,
    cancel: () => {
      engine.pause();
      finish('cancelled');
    },
    done,
  };
}
